import { supabase } from '@/lib/supabase';

// Loading quiz content from the database instead of src/data/*.js.
//
// Only published questions come back: RLS on the questions table restricts
// agents to status = 'active', so Confluence drafts awaiting review are
// invisible here no matter what this file asks for.

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Counts for the track cards and topic picker, in one round trip.
 * Returns { report, monthly, byTopic: { billing: 3, ... }, generalTotal }
 */
export async function fetchQuestionCounts() {
  const { data, error } = await supabase.rpc('get_question_counts');

  if (error) {
    console.error('Loading question counts failed:', error.message);
    return { report: 0, monthly: 0, byTopic: {}, generalTotal: 0 };
  }

  const counts = { report: 0, monthly: 0, byTopic: {}, generalTotal: 0 };

  for (const row of data || []) {
    const n = Number(row.question_count) || 0;
    if (row.track === 'report') counts.report += n;
    else if (row.track === 'monthly') counts.monthly += n;
    else if (row.track === 'general') {
      counts.byTopic[row.topic] = n;
      counts.generalTotal += n;
    }
  }

  return counts;
}

/**
 * Every published question for a track, optionally narrowed to one topic.
 * Returns the payloads themselves, in the shape the quiz components render.
 */
export async function fetchQuestions({ track, topic = null }) {
  let query = supabase
    .from('questions')
    .select('id, payload')
    .eq('status', 'active')
    .eq('track', track);

  if (topic) query = query.eq('topic', topic);

  const { data, error } = await query;

  if (error) {
    console.error('Loading questions failed:', error.message);
    return [];
  }

  // The database id becomes the question id, so answers key off something
  // stable even if two seeded questions ever shared an old numeric id.
  return (data || []).map((row) => ({ ...row.payload, id: row.id }));
}

/**
 * A mixed general session: a spread across every topic rather than a deep
 * dive into one. Samples a couple from each topic, then shuffles and caps,
 * which is the behaviour buildGeneralSet() had in the old data file.
 */
export async function fetchMixedGeneral({ perTopic = 2, limit = 12 } = {}) {
  const { data, error } = await supabase
    .from('questions')
    .select('id, topic, payload')
    .eq('status', 'active')
    .eq('track', 'general');

  if (error) {
    console.error('Loading mixed questions failed:', error.message);
    return [];
  }

  const byTopic = {};
  for (const row of data || []) {
    (byTopic[row.topic] ||= []).push({ ...row.payload, id: row.id });
  }

  // Round-robin so no single topic dominates a short session.
  const picked = [];
  for (let i = 0; i < perTopic; i++) {
    for (const topic of Object.keys(byTopic)) {
      const q = byTopic[topic][i];
      if (q) picked.push(q);
    }
  }

  return shuffle(picked).slice(0, limit);
}
